/**
=========================================================
* Live Auctions Landing Page
=========================================================
*/

import { useState, useEffect } from "react";
import React from 'react';

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import { Helmet } from "react-helmet";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import RefreshIcon from "@mui/icons-material/Refresh";
import HomeIcon from "@mui/icons-material/Home";
import { useNavigate } from "react-router-dom";

// API
import { liveAuctionsAPI } from "services/api";

export default function LiveAuctions() {
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchActiveAuctions();
  }, []);

  const fetchActiveAuctions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await liveAuctionsAPI.getActiveRecentAuctions();
      
      if (response.data.status === 1) {
        setAuctions(response.data.auctions || []);
      } else {
        setError(response.data.message || 'Failed to fetch auctions');
      }
    } catch (err) {
      console.error('Error fetching auctions:', err);
      setError('Failed to load live auctions. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateTimeString) => {
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return dateTimeString;
    }
  };

  const getTimeAgo = (dateTimeString) => {
    try {
      const date = new Date(dateTimeString);
      const now = new Date();
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));
      
      if (diffInMinutes < 1) return 'Just started';
      if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
      
      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours < 24) return `${diffInHours} hours ago`;
      
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays} days ago`;
    } catch (error) {
      return 'Unknown time';
    }
  };

  const handleJoinAuction = (auctionLink) => {
    if (auctionLink) {
      window.open(auctionLink, '_blank');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      <Helmet>
        <title>Live Auctions - {global.config.app.name}</title>
        <meta
          name="description"
          content="Join live cricket auctions and be part of the action. Active auctions updated in real-time."
        />
        <meta
          name="keywords"
          content="Live auctions, cricket, bidding, active auctions, real-time"
        />
      </Helmet>

      {/* Header */}
      <AppBar position="static" sx={{ backgroundColor: '#1976d2', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/')}
            sx={{ mr: 2 }}
          >
            <HomeIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            🏏 Live Cricket Auctions
          </Typography>
          <IconButton
            color="inherit"
            onClick={fetchActiveAuctions}
            disabled={loading}
          >
            <RefreshIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box textAlign="center" mb={4}>
          <Typography variant="h4" fontWeight="bold" color="text.primary" gutterBottom>
            Active Auctions
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Join live cricket auctions happening right now
          </Typography>
        </Box>

        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress size={60} />
            <Typography variant="body2" color="text.secondary" ml={2}>
              Loading live auctions...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
            <Button 
              variant="text" 
              color="error" 
              size="small" 
              onClick={fetchActiveAuctions}
              sx={{ ml: 2 }}
            >
              Retry
            </Button>
          </Alert>
        )}

        {!loading && !error && auctions.length === 0 && (
          <Card sx={{ p: 4, textAlign: 'center', maxWidth: 500, mx: 'auto' }}>
            <Typography variant="h5" color="text.primary" mb={2}>
              🏏 No Active Auctions
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={3}>
              There are currently no active auctions. Check back later for new auctions!
            </Typography>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={fetchActiveAuctions}
              startIcon={<RefreshIcon />}
            >
              Refresh
            </Button>
          </Card>
        )}

        {!loading && !error && auctions.length > 0 && (
          <>
            <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
              <Typography variant="h6" color="text.primary" fontWeight="bold">
                {auctions.length} Active Auction{auctions.length !== 1 ? 's' : ''}
              </Typography>
              <Button 
                variant="outlined" 
                color="primary" 
                size="small"
                onClick={fetchActiveAuctions}
                startIcon={<RefreshIcon />}
              >
                Refresh
              </Button>
            </Box>

            <Grid container spacing={3}>
              {auctions.map((auction, index) => (
                <Grid item xs={12} sm={6} lg={4} key={auction.series_id || index}>
                  <Card 
                    sx={{ 
                      p: 3, 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 2,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" mb={2}>
                      {auction.league_logo && (
                        <Avatar
                          src={auction.league_logo}
                          alt={auction.series_name}
                          sx={{ width: 50, height: 50, mr: 2 }}
                        />
                      )}
                      <Box flex={1}>
                        <Typography variant="h6" color="text.primary" fontWeight="bold" gutterBottom>
                          {auction.series_name}
                        </Typography>
                        <Chip 
                          label="LIVE" 
                          color="error" 
                          size="small" 
                          sx={{ fontWeight: 'bold' }}
                        />
                      </Box>
                    </Box>

                    <Box mb={2}>
                      <Typography variant="body2" color="text.primary" fontWeight="medium" gutterBottom>
                        Started: {formatDateTime(auction.auction_started_on)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" fontSize="0.875rem">
                        {getTimeAgo(auction.auction_started_on)}
                      </Typography>
                    </Box>

                    <Box mt="auto">
                      <Button
                        variant="contained"
                        color="success"
                        fullWidth
                        onClick={() => handleJoinAuction(auction.auction_link)}
                        sx={{ 
                          py: 1.5,
                          fontSize: '1rem',
                          fontWeight: 'bold',
                          borderRadius: 2,
                          textTransform: 'none'
                        }}
                      >
                        🚀 Join Auction
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        <Box mt={4} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            Auctions are updated in real-time. Last updated: {new Date().toLocaleTimeString()}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
